import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Sparkles } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { AssetDot } from '@/shared/components/AssetDot';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import { formatDate } from '@/shared/lib/format';
import type { SearchResult } from '../api/libraryApi';
import { Highlighted } from './Highlighted';

const TAGS_SHOWN = 2;

type Props = { doc: SearchResult; terms: string[]; index: number };

/** One report: kind and date, title, its key call (or the matching sentence), who wrote it, tags, and Ask (the
 * report's own Ask tab, answered from this report alone). */
export function ReportCard({ doc, terms, index }: Props) {
  const extra = doc.currencies.length - TAGS_SHOWN;
  return (
    <Stack component="article" aria-label={doc.title} sx={{ ...cardSx, animationDelay: `${Math.min(index, 10) * 35}ms` }}>
      <Stack sx={{ gap: 0.75, px: 2, pt: 1.75, pb: 1.5, flexGrow: 1 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
          <AssetDot assetClass={doc.assetClass} />
          <Typography variant="caption" noWrap sx={{ flexGrow: 1, color: 'text.primary' }}>{doc.series}</Typography>
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>{formatDate(doc.publishDate)}</Typography>
        </Stack>
        <Link component={RouterLink} to={`/documents/${doc.id}`} underline="none" sx={titleSx}>{doc.title}</Link>
        {doc.snippet ? (
          <Typography variant="body2" sx={snippetSx}>
            <Highlighted text={doc.snippet} terms={terms} />
          </Typography>
        ) : null}
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
        <Stack direction="row" sx={{ flexShrink: 0, '& > * + *': { ml: -0.75 } }}>
          {doc.analysts.slice(0, 2).map((a) => <PersonAvatar key={a} name={a} size={22} ring />)}
        </Stack>
        <Typography variant="caption" noWrap sx={{ flexGrow: 1, minWidth: 0, color: 'text.secondary' }}>{doc.analysts.join(', ')}</Typography>
        {doc.currencies.slice(0, TAGS_SHOWN).map((c) => <Box key={c} component="span" sx={tagSx}>{c}</Box>)}
        {extra > 0 ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>+{extra}</Typography> : null}
        <Button size="small" variant="outlined" startIcon={<Sparkles size={13} />} component={RouterLink} to={`/documents/${doc.id}?tab=ask`} aria-label={`Ask about ${doc.title}`} sx={askSx}>
          Ask
        </Button>
      </Stack>
    </Stack>
  );
}

const cardSx = {
  position: 'relative',
  height: '100%',
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
  transition: 'transform 180ms cubic-bezier(0.2, 0, 0, 1), box-shadow 180ms, border-color 180ms',
  '&:hover, &:focus-within': { transform: 'translateY(-2px)', borderColor: 'surface.border', boxShadow: '0 6px 20px rgba(26, 26, 25, 0.07)' },
  '@keyframes cardIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'cardIn 280ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', '&:hover, &:focus-within': { transform: 'none' } },
} as const;

// The title's overlay makes the whole card open the report; Ask sits above it.
const titleSx = {
  fontSize: '0.9375rem',
  fontWeight: 600,
  lineHeight: 1.35,
  color: 'text.primary',
  '&::after': { content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit' },
  '&:focus-visible': { outline: 'none' },
  '&:focus-visible::after': { outline: 2, outlineStyle: 'solid', outlineColor: 'primary.main', outlineOffset: 2 },
} as const;

const snippetSx = { color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as const;

const tagSx = { px: 0.75, py: 0.25, borderRadius: 0.75, fontSize: '0.6875rem', fontWeight: 500, bgcolor: 'surface.sunken', whiteSpace: 'nowrap' } as const;

const askSx = {
  position: 'relative',
  zIndex: 1,
  flexShrink: 0,
  minHeight: 28,
  px: 1,
  fontSize: '0.75rem',
  color: 'text.primary',
  borderColor: 'divider',
  '& .MuiButton-startIcon': { mr: 0.5 },
  '&:hover': { borderColor: 'primary.light', color: 'primary.main', bgcolor: 'background.paper' },
} as const;
